import React, { useEffect, useState } from "react";
import { Calendar, ExternalLink, Loader2, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import appConfig from "../../../config/appConfig";
import { LIJJAT_BRAND } from "../shared/lijjatBrandAssets";

const COLORS = {
  primary: "#d35400",
  lightYellow: "#FFE699",
  lightBlue: "#BDD7EE",
  border: "#999999",
  muted: "#777777",
};

const LijjatScanHistoryPage = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(`${appConfig.apiBaseUrl}/getUserLocations`);
        const data = await res.json();

        if (!data.success) {
          setError("Failed to fetch location data");
          return;
        }

        setLocations(data.data || []);
      } catch (err) {
        console.error("Error:", err);
        setError("Unable to load location data");
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, []);

  const handleViewProduct = (rsnId) => {
    if (rsnId) {
      navigate(`/lijjat/scanned-products/${rsnId}`);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "#f9f9f9", fontFamily: "'Roboto', sans-serif" }}
    >
      <header
        className="flex items-center justify-center px-6 py-4 shadow-sm"
        style={{ backgroundColor: COLORS.primary, color: "white" }}
      >
        <img src={LIJJAT_BRAND.logo} alt="Lijjat Logo" className="mr-3 h-10 md:h-12" />
        <h1 className="text-center text-lg font-bold leading-tight md:text-xl">
          {LIJJAT_BRAND.companyName}
        </h1>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        <section className="overflow-hidden rounded-lg bg-white shadow-lg">
          <div
            className="px-6 py-4 text-center"
            style={{ backgroundColor: COLORS.primary, color: "white" }}
          >
            <h2 className="text-xl font-semibold md:text-2xl">Product Scan History</h2>
          </div>

          {loading && (
            <div className="flex flex-col items-center py-12">
              <Loader2
                className="mb-3 h-8 w-8 animate-spin"
                style={{ color: COLORS.primary }}
              />
              <p className="text-gray-600">Loading location data...</p>
            </div>
          )}

          {error && <div className="py-8 text-center font-medium text-red-600">{error}</div>}

          {!loading && !error && locations.length === 0 && (
            <div className="py-8 text-center text-gray-500">No location data found.</div>
          )}

          {!loading && !error && locations.length > 0 && (
            <>
              <div className="space-y-4 p-4 md:hidden">
                {locations.map((loc, index) => (
                  <div
                    key={loc.LocationID}
                    className="relative rounded-lg border p-4 shadow-sm"
                    style={{
                      backgroundColor:
                        index % 2 === 0 ? COLORS.lightYellow : COLORS.lightBlue,
                      borderColor: COLORS.border,
                    }}
                  >
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <MapPin
                          className="mt-0.5 h-4 w-4 flex-shrink-0"
                          style={{ color: COLORS.primary }}
                        />
                        <div>
                          <p className="font-medium">
                            {loc.City || "N/A"}, {loc.State || "N/A"}
                          </p>
                          <p className="text-xs text-gray-600">{loc.Area || "N/A"}</p>
                        </div>
                      </div>

                      <div className="line-clamp-2 text-xs text-gray-600">
                        {loc.FullAddress || "N/A"}
                      </div>

                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar className="h-3 w-3" />
                        <span>{new Date(loc.CreatedAt).toLocaleString()}</span>
                      </div>
                    </div>

                    {loc.RSNID && (
                      <button
                        onClick={() => handleViewProduct(loc.RSNID)}
                        className="absolute right-3 top-3 rounded-full bg-white p-2 shadow-md transition-shadow hover:shadow-lg"
                        title="View Product Details"
                      >
                        <ExternalLink
                          className="h-4 w-4"
                          style={{ color: COLORS.primary }}
                        />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full">
                  <thead style={{ backgroundColor: COLORS.primary, color: "white" }}>
                    <tr>
                      {["Area", "City", "State", "Full Address", "Timestamp", ""].map(
                        (heading) => (
                          <th
                            key={heading}
                            className="px-4 py-3 text-left text-sm font-medium uppercase tracking-wider"
                          >
                            {heading}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {locations.map((loc, index) => (
                      <tr
                        key={loc.LocationID}
                        className="border-t"
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? COLORS.lightYellow : COLORS.lightBlue,
                          borderColor: COLORS.border,
                        }}
                      >
                        <td className="px-4 py-3 text-sm">{loc.Area || "N/A"}</td>
                        <td className="px-4 py-3 text-sm">{loc.City || "N/A"}</td>
                        <td className="px-4 py-3 text-sm">{loc.State || "N/A"}</td>
                        <td className="max-w-xs truncate px-4 py-3 text-sm" title={loc.FullAddress}>
                          {loc.FullAddress || "N/A"}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {new Date(loc.CreatedAt).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {loc.RSNID ? (
                            <button
                              onClick={() => handleViewProduct(loc.RSNID)}
                              className="rounded-full bg-white p-2 shadow transition-shadow hover:shadow-md"
                              title="View Product Details"
                            >
                              <ExternalLink
                                className="h-4 w-4"
                                style={{ color: COLORS.primary }}
                              />
                            </button>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>

        <footer className="mt-10 text-center text-xs" style={{ color: COLORS.muted }}>
          <p>
            Developed by: <strong>Shubham Automation Pvt. Ltd.</strong>
          </p>
        </footer>
      </main>
    </div>
  );
};

export default LijjatScanHistoryPage;
